// Polyfill requestIdleCallback if not available
export const requestIdleCallback =
  typeof window.requestIdleCallback === "function"
    ? window.requestIdleCallback
    : setTimeout;

// Check if browser supports view transitions
export function supportsViewTransitions() {
  return typeof document.startViewTransition === "function";
}

// Global reference to current view transition
export const viewTransition = { current: undefined };

// View transition types for animations
const viewTransitionTypes = {
  "product-grid": async () => {
    const grid = document.querySelector(".product-grid");
    const items = [...document.querySelectorAll(".product-grid .product-grid__item")];

    if (!grid || items.length === 0) return;

    return await new Promise((resolve) => {
      requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(grid, items);

        items.forEach((item, index) => {
          if (index < cardsToAnimate) {
            item.style.setProperty(
              "view-transition-name",
              `product-card-${item.dataset.productId}`
            );
          } else {
            item.style.setProperty("content-visibility", "hidden");
          }
        });

        resolve(null);
      });
    });
  },

  cleanup: (items) => {
    items.forEach((item) => {
      item.style.removeProperty("view-transition-name");
      item.style.removeProperty("content-visibility");
    });
  },
};

// Start a view transition with optional animation types
export function startViewTransition(callback, types) {
  return new Promise(async (resolve) => {
    if (supportsViewTransitions() && !prefersReducedMotion()) {
      const cleanupFunctions = [];

      if (types) {
        for (const type of types) {
          if (viewTransitionTypes[type]) {
            const fn = await viewTransitionTypes[type]();
            if (fn) cleanupFunctions.push(fn);
          }
        }
      }

      const transition = document.startViewTransition(callback);
      if (!viewTransition.current) viewTransition.current = transition.finished;

      if (types) types.forEach((type) => transition.types.add(type));

      transition.finished.then(() => {
        viewTransition.current = undefined;
        cleanupFunctions.forEach((fn) => fn());
        resolve();
      });

      return;
    }

    // Fallback if transitions not supported
    callback();
    resolve();
  });
}

// Fetch request configuration helper
export function fetchConfig(type = "json", options = {}) {
  const headers = {
    "Content-Type": "application/json",
    Accept: `application/${type}`,
    ...options.headers,
  };

  // Special case for JavaScript fetch
  if (type === "javascript") {
    headers["X-Requested-With"] = "XMLHttpRequest";
    delete headers["Content-Type"];
  }

  return {
    method: "POST",
    headers,
    body: options.body,
  };
}

// Utility functions for event handling
export function debounce(fn, delay) {
  let timer;
  function debounced(...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  }
  debounced.cancel = () => clearTimeout(timer);
  return debounced;
}

export function throttle(fn, limit) {
  let last = 0;
  function throttled(...args) {
    const now = performance.now();
    if (now - last >= limit) {
      last = now;
      fn.apply(this, args);
    }
  }
  throttled.cancel = () => (last = performance.now());
  return throttled;
}

// Detect prefers-reduced-motion
const reducedMotion = matchMedia("(prefers-reduced-motion: reduce)");
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

// String utilities
export function normalizeString(str) {
  return str.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

// Money formatting
export function formatMoney(value) {
  let str = value.replace(" ", "");
  if (str.indexOf(",") === -1) return str;
  if (str.indexOf(",") < str.indexOf(".")) return str.replace(",", "");
  if (str.indexOf(".") < str.indexOf(",")) return str.replace(".", "").replace(",", ".");
  return str.replace(",", ".");
}

// Document ready
export function onDocumentReady(callback) {
  if (document.readyState === "complete") callback();
  else window.addEventListener("load", callback);
}

// Wait for animations to finish
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = (Array.isArray(elements) ? elements.flatMap((el) => el.getAnimations(options)) : elements.getAnimations(options))
    .reduce((arr, anim) => {
      if (anim.timeline instanceof DocumentTimeline) arr.push(anim.finished);
      return arr;
    }, []);

  return Promise.allSettled(animations).then(callback);
}

// Click detection helpers
export function isClickedOutside(event, container) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) return !isPointWithinElement(event.clientX, event.clientY, container);
  return !container.contains(event.target);
}

export function isPointWithinElement(x, y, element) {
  const rect = element.getBoundingClientRect();
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

// Media query helpers
export const mediaQueryLarge = matchMedia("(width >= 750px)");
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

// Math utilities
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

export function center(element, axis) {
  const rect = element.getBoundingClientRect();
  const centerPoint = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  return axis ? centerPoint[axis] : centerPoint;
}

export function start(element, axis) {
  const rect = element.getBoundingClientRect();
  const point = { x: rect.left, y: rect.top };
  return axis ? point[axis] : point;
}

export function closest(array, value) {
  return array.reduce((closestVal, current) => (Math.abs(current - value) < Math.abs(closestVal - value) ? current : closestVal));
}

export function preventDefault(event) {
  event.preventDefault();
}

// Get visible elements within a container
export function getVisibleElements(container, elements, minVisibility = 1, axis = "y") {
  if (!elements?.length) return [];
  const rect = container.getBoundingClientRect();

  return elements.filter((el) => {
    const elRect = el.getBoundingClientRect();
    if (minVisibility < 1) {
      const overlapX = Math.max(0, Math.min(rect.right, elRect.right) - Math.max(rect.left, elRect.left));
      const overlapY = Math.max(0, Math.min(rect.bottom, elRect.bottom) - Math.max(rect.top, elRect.top));
      const elArea = elRect.width * elRect.height;
      if (axis === "x") return elRect.width > 0 && overlapX / elRect.width >= minVisibility;
      if (axis === "y") return elRect.height > 0 && overlapY / elRect.height >= minVisibility;
      return elArea > 0 && (overlapX * overlapY) / elArea >= minVisibility;
    }

    const fullyVisibleX = elRect.left >= rect.left && elRect.right <= rect.right;
    const fullyVisibleY = elRect.top >= rect.top && elRect.bottom <= rect.bottom;

    return axis === "x" ? fullyVisibleX : fullyVisibleY || fullyVisibleX;
  });
}

// Helper to calculate how many cards to animate in a product grid
function getCardsToAnimate(grid, items) {
  if (!grid || !items || items.length === 0) return 0;
  const firstItem = items[0];
  if (!firstItem) return 0;

  const gridRect = grid.getBoundingClientRect();
  const visibleHeight = Math.round(Math.min(window.innerHeight, gridRect.bottom) - Math.max(0, gridRect.top));
  if (visibleHeight <= 0) return 0;

  const card = firstItem.querySelector("product-card");
  const style = getComputedStyle(grid);
  const ratioStr = card?.refs?.cardGallery?.style.getPropertyValue("--gallery-aspect-ratio") || "";
  let aspectRatio = parseFloat(ratioStr) || 0.5;

  if (ratioStr?.includes("/")) {
    const [w, h] = ratioStr.split("/").map(Number);
    aspectRatio = w / h;
  }

  const gap = parseInt(card?.refs?.productCardLink?.style.getPropertyValue("--product-card-gap") || "12") || 12;
  const gridGap = parseInt(style.getPropertyValue("--product-grid-gap")) || 12;
  const padding = 2 * ((parseInt(style.fontSize) || 16) + 2);

  const cardWidth = window.innerWidth < 750 ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + gap + padding;

  const columns = window.innerWidth < 750 ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rows = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));

  return columns * rows;
}

// Preload image
export function preloadImage(src) {
  new Image().src = src;
}

// Text shimmer component
export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute("shimmer", "");
  }
}
if (!customElements.get("text-component")) {
  customElements.define("text-component", TextComponent);
}

export function resetShimmer(root = document.body) {
  root.querySelectorAll("[shimmer]").forEach((el) => el.removeAttribute("shimmer"));
}

// Change the browser theme color dynamically
export function changeMetaThemeColor(element) {
  const meta = document.head.querySelector('meta[name="theme-color"]');
  const style = window.getComputedStyle(element);
  if (meta) meta.setAttribute("content", style.backgroundColor);
}

// Scheduler for deferred execution
class Scheduler {
  #tasks = new Set();
  #running = false;

  async schedule(task) {
    this.#tasks.add(task);
    if (!this.#running) {
      this.#running = true;
      if (viewTransition.current) await viewTransition.current;
      requestAnimationFrame(this.flush);
    }
  }

  flush = () => {
    for (const task of this.#tasks) task();
    this.#tasks.clear();
    this.#running = false;
  };
}

export const scheduler = new Scheduler();

// Attach scheduler to Theme utilities
Theme.utilities = {
  ...Theme.utilities,
  scheduler,
};
